import React, { Component } from "react";
import * as Icon from "react-feather";
import moment from "moment";
import GmailService from "../../services/GmailService";
import UserService from "../../services/UserService";
import "./inbox-settings.css";

class InboxSettingsPage extends Component {
	constructor(props) {
		super(props);
		this.state = {
			teams: [],
			accounts: [],
			loading: true,
			connectingTeamId: null,
			disconnectingId: null,
			toast: null,
		};
	}

	async componentDidMount() {
		window.addEventListener("message", this.onPopupMessage);
		await this.refresh();
	}

	componentWillUnmount() {
		window.removeEventListener("message", this.onPopupMessage);
	}

	onPopupMessage = event => {
		const data = event && event.data;
		if (!data || data.type !== "gmail-oauth-result") return;
		const payload = data.payload || {};
		if (payload.ok) {
			this.setState({ toast: { type: "success", message: "Mailbox connected." } });
			this.refresh();
		} else {
			this.setState({ toast: { type: "error", message: payload.message || "Connection failed." } });
		}
	};

	refresh = async () => {
		this.setState({ loading: true });
		const [teams, accounts] = await Promise.all([UserService.fetchTeams({ search: "" }), GmailService.listAccounts()]);
		this.setState({ teams: teams || [], accounts: accounts || [], loading: false });
	};

	connect = async team => {
		this.setState({ connectingTeamId: team.id, toast: null });
		const url = await GmailService.getAuthUrl(team.id);
		this.setState({ connectingTeamId: null });
		if (!url) {
			this.setState({ toast: { type: "error", message: "Could not build OAuth URL. Check Gmail configuration." } });
			return;
		}
		const w = 520, h = 640;
		const left = window.screenX + (window.outerWidth - w) / 2;
		const top = window.screenY + (window.outerHeight - h) / 2;
		window.open(url, "gmail-oauth", `width=${w},height=${h},left=${left},top=${top}`);
	};

	disconnect = async account => {
		if (!window.confirm(`Disconnect ${account.email_address}? Team members will lose inbox access until reconnected.`)) {
			return;
		}
		this.setState({ disconnectingId: account.id, toast: null });
		const result = await GmailService.disconnect(account.id);
		this.setState({ disconnectingId: null });
		if (result) {
			this.setState({ toast: { type: "success", message: "Disconnected." } });
			this.refresh();
		} else {
			this.setState({ toast: { type: "error", message: "Disconnect failed." } });
		}
	};

	render() {
		const { teams, accounts, loading, connectingTeamId, disconnectingId, toast } = this.state;

		const accountByTeam = {};
		accounts.forEach(a => {
			accountByTeam[a.team_id] = a;
		});

		return (
			<div className="container inbox-settings-page">
				<div className="inbox-settings-header">
					<h1>Inbox &mdash; Gmail Connections</h1>
					<p>Connect a shared Gmail mailbox for each team. Only members of the team will be able to access it.</p>
				</div>

				{toast && (
					<div className={`inbox-settings-toast inbox-settings-toast--${toast.type}`}>
						{toast.message}
						<button onClick={() => this.setState({ toast: null })}>
							<Icon.X size={14} />
						</button>
					</div>
				)}

				{loading ? (
					<p>Loading&hellip;</p>
				) : teams.length === 0 ? (
					<p>No teams found. Create a team first under Users.</p>
				) : (
					<table className="inbox-settings-table">
						<thead>
							<tr>
								<th>Team</th>
								<th>Mailbox</th>
								<th>Status</th>
								<th>Connected</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{teams.map(team => {
								const account = accountByTeam[team.id];
								return (
									<tr key={team.id}>
										<td>{team.name}</td>
										<td>{account ? account.email_address : <span className="muted">&mdash;</span>}</td>
										<td>
											{account ? (
												<span className={`status-pill status-pill--${account.status}`}>{account.status}</span>
											) : (
												<span className="muted">not connected</span>
											)}
										</td>
										<td>{account ? moment(account.updated_at).format("MMM D, YYYY h:mm A") : "—"}</td>
										<td className="actions">
											{!account || account.status !== "active" ? (
												<button
													className="btn-primary"
													onClick={() => this.connect(team)}
													disabled={connectingTeamId === team.id}
												>
													<Icon.Link size={14} /> {connectingTeamId === team.id ? "Opening…" : account ? "Reconnect" : "Connect Gmail"}
												</button>
											) : (
												<button
													className="btn-danger"
													onClick={() => this.disconnect(account)}
													disabled={disconnectingId === account.id}
												>
													<Icon.XCircle size={14} /> {disconnectingId === account.id ? "Disconnecting…" : "Disconnect"}
												</button>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				)}
			</div>
		);
	}
}

export default InboxSettingsPage;
